import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { universitiesI18n } from "../i18n/universities_i18n";

const data = {
  UK: {
    flag: "🇬🇧",
    universities: ["University of Oxford", "University of Cambridge", "Imperial College London", "King's College London", "London School of Economics", "University of York", "Queen Mary University of London", "University of Bristol", "University of Manchester", "City University of London", "Bayes Business School", "INTO London"],
    schools: ["Bournemouth Collegiate School", "Bosworth Independent School", "The Worthgate School", "CATS Cambridge", "Cambridge School of Visual & Performing Arts", "TASIS The American School England", "CATS Academy Boston", "The Village School", "North Broward Preparatory School", "Trinity School", "Windermere Preparatory School"]
  },
  USA: {
    flag: "🇺🇸",
    universities: ["Stanford University", "Columbia University", "UCLA", "Boston University", "University of Miami", "New York University", "University of South Carolina", "University of Massachusetts", "Pace University", "American University", "University of Arizona", "University of Alabama at Birmingham", "Drew University", "Florida International University", "Chicago University"],
    schools: ["North Broward Preparatory School (Florida)", "The Village School (Texas)", "Windermere Preparatory School (Florida)"]
  },
  NL: {
    flag: "🇳🇱",
    universities: ["University of Amsterdam", "Delft University of Technology", "Erasmus University Rotterdam", "University of Groningen", "The Hague University of Applied Sciences", "ArtEZ University of the Arts", "Rotterdam University of Applied Sciences", "Twente University"],
    schools: []
  },
  CH: {
    flag: "🇨🇭",
    universities: ["Les Roches Global Hospitality Education", "Glion Institute of Higher Education"],
    schools: ["Collège du Léman", "Collège Beau Soleil", "Collège Champittet", "Hamelin-Laie International School"]
  },
  ES: {
    flag: "🇪🇸",
    universities: ["IE University", "ESADE Business School", "Universidad de Navarra"],
    schools: ["Sotogrande International School", "Aquinas American School", "Yago Boarding Schools Spain", "Globeducate Schools Agora International School"]
  },
  IE: {
    flag: "🇮🇪",
    universities: ["Trinity College Dublin", "University College Dublin", "Griffith College"],
    schools: []
  },
  FR: {
    flag: "🇫🇷",
    universities: ["Istituto Marangoni Paris", "IESEG School of Management", "IFA Paris", "Sorbonne University", "Toulouse Business School", "UE University of Applied Sciences Europe"],
    schools: []
  },
  DE: { flag: "🇩🇪", universities: ["SRH University Heidelberg"], schools: [] },
  AT: { flag: "🇦🇹", universities: ["Webster University Vienna", "Modul University Vienna"], schools: [] },
  PL: { flag: "🇵🇱", universities: ["Kozminski University", "Vistula University", "Civitas University of Warsaw"], schools: [] },
  CZ: { flag: "🇨🇿", universities: ["University of New York in Prague", "Prague College"], schools: [] },
  CA: { flag: "🇨🇦", universities: ["Humber College", "Seneca College"], schools: [] }
};

const tabs = [
  { id: "all", labelKey: "allTab" },
  { id: "UK", label: "🇬🇧 UK" },
  { id: "USA", label: "🇺🇸 USA" },
  { id: "NL", label: "🇳🇱 NL" },
  { id: "CH", label: "🇨🇭 CH" },
  { id: "ES", label: "🇪🇸 ES" },
  { id: "IE", label: "🇮🇪 IE" },
  { id: "FR", label: "🇫🇷 FR" },
  { id: "DE", label: "🇩🇪 DE" },
  { id: "AT", label: "🇦🇹 AT" },
  { id: "PL", label: "🇵🇱 PL" },
  { id: "CZ", label: "🇨🇿 CZ" },
  { id: "CA", label: "🇨🇦 CA" }
];

const Universities = ({ onOpenForm }) => {
  const { lang } = useLang();
  const i18n = universitiesI18n[lang] || universitiesI18n.ru;
  const isRtl = lang === 'ar';
  const [active, setActive] = useState("all");
  const [expanded, setExpanded] = useState(false);

  const items = active === "all"
    ? Object.entries(data).flatMap(([code, c]) => [
        ...c.universities.map(name => ({ name, country: code, flag: c.flag, type: 'uni' })),
        ...c.schools.map(name => ({ name, country: code, flag: c.flag, type: 'school' }))
      ])
    : (data[active] ? [
        ...data[active].universities.map(name => ({ name, country: active, flag: data[active].flag, type: 'uni' })),
        ...data[active].schools.map(name => ({ name, country: active, flag: data[active].flag, type: 'school' }))
      ] : []);

  const LIMIT = 24;
  const visibleItems = expanded ? items : items.slice(0, LIMIT);
  const canToggle = items.length > LIMIT;

  // Reset expanded state when tab changes
  const switchTab = (id) => { setActive(id); setExpanded(false); };

  return (
    <section id="universities" className="py-20 md:py-28" style={{ background: '#FFFFFF' }} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="eyebrow mb-3">{i18n.eyebrow}</div>
          <h2 className="headline-huge text-3xl md:text-5xl text-balance" style={{ color: '#1B2A4A' }}>{i18n.title}</h2>
          <p className="mt-3" style={{ color: '#666666' }}>{i18n.subtitle}</p>
        </div>

        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className="px-4 h-9 rounded-full text-sm font-semibold transition border"
              style={active === t.id
                ? { background: '#1B2A4A', color: '#fff', borderColor: '#1B2A4A' }
                : { background: '#fff', color: '#1B2A4A', borderColor: '#E8E0D5' }}
              data-testid={`universities-tab-${t.id}`}
            >
              {t.labelKey ? i18n[t.labelKey] : t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2.5 justify-center max-w-6xl mx-auto">
          {visibleItems.map((it, i) => (
            <div
              key={`${it.name}-${i}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium select-none"
              style={{ background: '#fff', color: '#1B2A4A', border: '1px solid #E8E0D5' }}
              data-testid={`uni-item-${i}`}
            >
              <span className="text-base leading-none">{it.flag}</span>
              <span>{it.name}</span>
              {it.type === 'school' && <span className="text-[10px] uppercase tracking-wider opacity-60">{i18n.schoolBadge}</span>}
            </div>
          ))}
        </div>

        {canToggle && (
          <div className="text-center mt-8">
            <button
              onClick={() => setExpanded(v => !v)}
              className="inline-flex items-center gap-2 text-sm md:text-base font-semibold transition hover:opacity-80"
              style={{ color: '#C9A96E' }}
              data-testid="universities-toggle-btn"
            >
              {expanded ? i18n.collapse : i18n.showAll(items.length)}
            </button>
          </div>
        )}

        <div className="text-center mt-12">
          <button onClick={onOpenForm} className="inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold transition hover:opacity-90 text-white" style={{ background: '#1B2A4A' }} data-testid="universities-cta-btn">
            {i18n.cta} <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Universities;
